import React from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/api";
import { ArrowUpRight, ArrowRight, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";

const LEARN_URL = "https://learn.ravemora.com/";
const WA_LEARNING = `https://wa.me/917483500550?text=${encodeURIComponent(
  "Hi Ravemora, I'm interested in your learning and mentoring programs. Please share the available programs, mentors, fees and how to get started."
)}`;
const LEARN_CAREERCOACH = "https://learn.ravemora.com/careercoach";
const LEARN_IT = "https://learn.ravemora.com/it-infra";
const LEARN_MENTOR = "https://learn.ravemora.com/become-a-mentor";

const counselling = [
  { n: "01", title: "Academic Counselling", line: "Guidance for students and parents navigating academic choices, subject selection, stream selection and education pathways." },
  { n: "02", title: "Career Counselling for Students", line: "Personalized career guidance to help students explore career fields, understand options and make informed decisions about their future." },
  { n: "03", title: "Career Guidance for Graduates", line: "Guidance for graduates evaluating career options, further development and professional pathways." },
  { n: "04", title: "Career Guidance for Freshers", line: "Support for fresh graduates and early-career professionals transitioning from education into the workplace." },
  { n: "05", title: "Career Assessment & Guidance", line: "Assessment-based guidance to help individuals understand interests, strengths, preferences and potential career directions." },
  { n: "06", title: "Parent–Student Career Counselling", line: "Structured counselling sessions that help students and parents discuss career interests, options and future plans." },
];

const coaching = [
  { n: "01", title: "One-to-One Mentoring", line: "Personalized live mentoring based on the learner's goals, experience and learning requirements." },
  { n: "02", title: "Group Mentoring", line: "Live group learning and mentoring led by experienced professionals." },
  { n: "03", title: "Professional Coaching", line: "Practical coaching designed to strengthen professional knowledge, capabilities and confidence." },
  { n: "04", title: "Expert-Led Learning", line: "Learn directly from professionals who bring real-world experience into the learning process." },
  { n: "05", title: "Practical Skills Development", line: "Build practical capabilities through guided learning, exercises, projects, problem-solving and real-world application." },
  { n: "06", title: "Career Mentoring", line: "Gain professional perspective and guidance from experienced practitioners." },
];

const itTraining = [
  { n: "01", title: "IT Infrastructure Training", line: "Practical training across enterprise IT infrastructure and the technologies that support modern organizations." },
  { n: "02", title: "Server Infrastructure Training", line: "Learn server infrastructure concepts, administration, troubleshooting and operational practices." },
  { n: "03", title: "Network Infrastructure Training", line: "Develop practical knowledge of networking, connectivity, routing, switching, troubleshooting and infrastructure operations." },
  { n: "04", title: "Network Security Training", line: "Build practical understanding of network security technologies, security controls and operational troubleshooting." },
  { n: "05", title: "Cybersecurity Training", line: "Develop practical cybersecurity knowledge and understand how security is applied in real-world IT environments." },
  { n: "06", title: "IT Infrastructure Mentorship", line: "One-to-one guidance for learners and professionals dealing with practical infrastructure challenges." },
  { n: "07", title: "Real-World IT Troubleshooting", line: "Learn structured approaches to diagnosing and resolving real-world IT infrastructure and technology problems." },
  { n: "08", title: "One-to-One IT Mentorship", line: "Personalized technical mentorship for learners and working IT professionals." },
];

const beyondTech = [
  "Business & Management", "Leadership", "Professional Development", "Communication Skills", "Language Learning",
  "Sales & Business Skills", "Personal Development", "Wellness", "Creative Skills", "Other Professional Skills",
];

const proSkills = [
  "Communication Skills", "Leadership Skills", "Workplace Skills", "Presentation Skills", "Business Skills",
  "Problem-Solving", "Professional Development", "Personal Effectiveness", "Language & Communication", "Other Industry Skills",
];

const howSteps = [
  { n: "01", title: "Explore", line: "Understand your learning goal, career direction or development need." },
  { n: "02", title: "Connect", line: "Find an experienced professional or mentor relevant to your learning objective." },
  { n: "03", title: "Learn", line: "Build knowledge and practical capability through personalized guidance." },
  { n: "04", title: "Apply", line: "Use what you learn in real-world situations, projects, work or career decisions." },
];

const audiences = [
  { title: "Students", line: "Explore academic options, career directions and future opportunities." },
  { title: "Graduates & Freshers", line: "Build practical capabilities, understand career options and prepare for the workplace." },
  { title: "Working Professionals", line: "Develop new skills, strengthen existing capabilities and learn from experienced practitioners." },
  { title: "Organizations", line: "Access practical learning, mentoring and professional development opportunities for employees and teams." },
];

const whyPoints = [
  { n: "01", title: "Learn From Experience", line: "Connect with professionals who understand real-world work." },
  { n: "02", title: "Personalized Guidance", line: "Learning can be tailored to the individual's goals, experience and requirements." },
  { n: "03", title: "Practical Learning", line: "Focus on applying knowledge rather than only consuming information." },
  { n: "04", title: "One-to-One & Group Options", line: "Choose personalized mentoring or learn with others through group sessions." },
  { n: "05", title: "Multiple Areas of Expertise", line: "Technology, business, professional skills, languages, wellness, creative skills and more." },
  { n: "06", title: "Flexible Online Learning", line: "Access learning and mentoring online from wherever you are." },
];

export default function Learning() {
  return (
    <div>
      <Seo
        path="/learning"
        exactTitle="Learning & Mentoring | Ravemora Global"
        image="https://ravemora.com/og/og-learning.jpg"
        description="Explore live online learning, mentoring and practical training from experienced professionals across technology, business, careers and other skills."
      />

      {/* 1 — HERO */}
      <section className="relative overflow-hidden" data-testid="learning-hero">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-6 lg:pt-10 pb-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-navy/60">Learning &amp; Development</div>
            <h1 className="serif-display mt-6 text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-navy">
              Learn From People <br />
              Who Know <span className="text-gold">The Work.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-navy/70 leading-relaxed">
              Learn at Ravemora Global connects learners with experienced professionals through personalized online coaching, mentoring, counselling and practical skills development across technology, business, professional skills, languages, wellness, creative skills and other areas of expertise.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="learning-hero-explore">Explore Learn at Ravemora <ArrowUpRight size={14}/></a>
              <Link to="/contact" className="btn-outline" data-testid="learning-hero-talk">Talk to Us</Link>
              <a href={WA_LEARNING} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="hero-whatsapp"><MessageCircle size={14}/> WhatsApp Us</a>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] w-full overflow-hidden border border-navy/10">
              <img src={IMAGES.learning} alt="Online learning and mentoring" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* 2 — INTRODUCTION */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28" data-testid="learning-intro">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <h2 className="serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight text-navy">
              Learning Built Around People, Experience and <span className="text-gold">Real-World Capability</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-lg leading-relaxed text-navy/80">
            <p>At Ravemora, we believe meaningful learning goes beyond courses and certificates.</p>
            <p>Our learning approach connects people with experienced professionals who can share practical knowledge, provide personalized guidance and help learners apply what they learn.</p>
            <p>Through Learn at Ravemora Global, we offer online one-to-one and group coaching, mentoring, academic counselling, career counselling and practical skills development across a growing range of fields.</p>
            <p>Whether someone is exploring a career, developing professional capabilities, learning a technical skill or seeking guidance from an experienced practitioner, our goal is to make learning more personal, practical and relevant.</p>
          </div>
        </div>
      </section>

      {/* 3 — ACADEMIC & CAREER COUNSELLING */}
      <section className="bg-slateBg py-24 lg:py-28" data-testid="learning-counselling">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="label-eyebrow text-navy/60">Counselling</div>
            <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">Academic &amp; Career Counselling</h2>
            <p className="serif-display text-xl md:text-2xl text-gold mt-4">Make informed decisions about the future.</p>
            <p className="mt-6 text-navy/70 leading-relaxed max-w-2xl">
              Personalized academic and career counselling for students, graduates and freshers, helping them understand their interests, strengths, options and possible directions.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {counselling.map((c) => (
              <div key={c.n} className="bg-offwhite border border-navy/10 p-8" data-testid={`counselling-${c.n}`}>
                <div className="serif-display text-gold text-2xl leading-none">{c.n}</div>
                <h3 className="serif-display text-2xl text-navy mt-5 leading-snug">{c.title}</h3>
                <p className="text-navy/70 mt-3 leading-relaxed">{c.line}</p>
              </div>
            ))}
          </div>
          <a href={LEARN_CAREERCOACH} target="_blank" rel="noopener noreferrer" className="btn-primary mt-12 inline-flex items-center gap-2" data-testid="counselling-cta">
            Explore Academic &amp; Career Counselling <ArrowUpRight size={14}/>
          </a>
        </div>
      </section>

      {/* 4 — COACHING & MENTORING */}
      <section className="bg-navy text-offwhite relative overflow-hidden grain" data-testid="learning-coaching">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28 relative z-10">
          <div className="max-w-3xl">
            <div className="label-eyebrow text-gold">Coaching &amp; Mentoring</div>
            <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight">Coaching &amp; Mentoring</h2>
            <p className="serif-display text-xl md:text-2xl text-gold mt-4">Learn directly from people who know the work.</p>
            <p className="mt-6 text-offwhite/70 leading-relaxed max-w-2xl">
              Personalized online coaching and mentoring that connects learners with experienced professionals and practitioners across different areas of expertise.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {coaching.map((c) => (
              <div key={c.n} className="border border-gold/30 p-8" data-testid={`coaching-${c.n}`}>
                <div className="serif-display text-gold text-2xl leading-none">{c.n}</div>
                <h3 className="serif-display text-2xl mt-5 leading-snug">{c.title}</h3>
                <p className="text-offwhite/70 mt-3 leading-relaxed">{c.line}</p>
              </div>
            ))}
          </div>
          <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" className="btn-gold mt-12 inline-flex items-center gap-2" data-testid="coaching-cta">
            Explore Coaching &amp; Mentoring <ArrowUpRight size={14}/>
          </a>
        </div>
      </section>

      {/* 5 — IT TRAINING */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28" data-testid="learning-it">
        <div className="max-w-3xl">
          <div className="label-eyebrow text-navy/60">Technology</div>
          <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">IT Training &amp; Technology Learning</h2>
          <p className="serif-display text-xl md:text-2xl text-gold mt-4">Build practical technology skills with guidance from experienced professionals.</p>
          <p className="mt-6 text-navy/70 leading-relaxed max-w-2xl">
            Practical IT training and mentoring designed for learners, freshers and working IT professionals who want to build real-world technical capability.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {itTraining.map((c) => (
            <div key={c.n} className="border-t border-navy pt-6" data-testid={`it-${c.n}`}>
              <div className="serif-display text-gold text-xl leading-none">{c.n}</div>
              <h3 className="serif-display text-xl text-navy mt-4 leading-snug">{c.title}</h3>
              <p className="text-navy/70 mt-3 text-sm leading-relaxed">{c.line}</p>
            </div>
          ))}
        </div>
        <a href={LEARN_IT} target="_blank" rel="noopener noreferrer" className="btn-primary mt-12 inline-flex items-center gap-2" data-testid="it-cta">
          Explore IT Infrastructure &amp; Cybersecurity Learning <ArrowUpRight size={14}/>
        </a>
      </section>

      {/* 6 — BEYOND TECHNOLOGY */}
      <section className="bg-slateBg py-24 lg:py-28" data-testid="learning-beyond">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="label-eyebrow text-navy/60">Non-IT &amp; Professional Learning</div>
            <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">Beyond Technology</h2>
            <p className="serif-display text-xl md:text-2xl text-gold mt-4">Learning opportunities across professional and personal skills.</p>
            <p className="mt-6 text-navy/70 leading-relaxed max-w-2xl">
              Learning at Ravemora is not limited to IT. We connect learners with experienced professionals across a growing range of non-technical and professional disciplines.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-14">
            {beyondTech.map((t) => (
              <div key={t} className="bg-offwhite border border-navy/10 px-6 py-5 flex items-center" data-testid={`beyond-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="serif-display text-lg text-navy leading-snug">{t}</span>
              </div>
            ))}
          </div>
          <p className="mt-10 text-navy/70 leading-relaxed max-w-2xl">
            Our mentor network is designed to grow with learner needs. Experienced professionals and practitioners can share knowledge through one-to-one mentoring, group mentoring and practical learning experiences.
          </p>
          <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" className="btn-primary mt-10 inline-flex items-center gap-2" data-testid="beyond-cta">
            Explore Learning Opportunities <ArrowUpRight size={14}/>
          </a>
        </div>
      </section>

      {/* 7 — PROFESSIONAL SKILLS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28" data-testid="learning-skills">
        <div className="max-w-3xl">
          <div className="label-eyebrow text-navy/60">Professional Skills</div>
          <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">Build Skills That Matter</h2>
          <p className="mt-6 text-navy/70 leading-relaxed max-w-2xl">
            Develop practical capabilities that support learning, career development and professional growth.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-10 mt-14">
          {proSkills.map((t) => (
            <div key={t} className="border-t border-gold pt-5" data-testid={`skill-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <span className="serif-display text-xl text-navy leading-snug">{t}</span>
            </div>
          ))}
        </div>
        <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" className="btn-primary mt-12 inline-flex items-center gap-2" data-testid="skills-cta">
          Explore Learning Opportunities <ArrowUpRight size={14}/>
        </a>
      </section>

      {/* 8 — HOW LEARNING WORKS */}
      <section className="bg-navy text-offwhite relative overflow-hidden grain" data-testid="learning-how">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28 relative z-10">
          <div className="label-eyebrow text-gold">How learning works</div>
          <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight">A More Personal <span className="text-gold">Way to Learn.</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-14 mt-16">
            {howSteps.map((s) => (
              <div key={s.n} className="border-t border-gold pt-8" data-testid={`how-step-${s.n}`}>
                <div className="serif-display text-gold text-5xl leading-none">{s.n}</div>
                <div className="serif-display text-2xl mt-6">{s.title}</div>
                <p className="text-offwhite/70 mt-3 leading-relaxed">{s.line}</p>
              </div>
            ))}
          </div>
          <p className="serif-display text-2xl text-gold mt-16">Learn from experience. Grow with guidance.</p>
        </div>
      </section>

      {/* 9 — WHO IS IT FOR */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28" data-testid="learning-audiences">
        <div className="max-w-3xl">
          <div className="label-eyebrow text-navy/60">Who is it for</div>
          <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">Learning for Different Stages of the Journey</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {audiences.map((a) => (
            <div key={a.title} className="border border-navy/10 p-8" data-testid={`audience-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <h3 className="serif-display text-2xl text-navy leading-snug">{a.title}</h3>
              <p className="text-navy/70 mt-4 leading-relaxed">{a.line}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 10 — WHY RAVEMORA LEARNING */}
      <section className="bg-slateBg py-24 lg:py-28" data-testid="learning-why">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="label-eyebrow text-navy/60">Why Ravemora Learning</div>
            <h2 className="serif-display text-4xl md:text-5xl mt-4 leading-tight text-navy">Why Learn <span className="text-gold">at Ravemora?</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 mt-14">
            {whyPoints.map((w) => (
              <div key={w.n} className="border-t border-navy pt-6" data-testid={`why-${w.n}`}>
                <div className="serif-display text-gold text-2xl leading-none">{w.n}</div>
                <h3 className="serif-display text-2xl text-navy mt-4 leading-snug">{w.title}</h3>
                <p className="text-navy/70 mt-3 leading-relaxed">{w.line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11 — LEARN AT RAVEMORA (CONVERSION) */}
      <section className="bg-navy text-offwhite relative overflow-hidden grain" data-testid="learning-platform">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32 relative z-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-gold">The learning platform</div>
            <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">Explore <span className="text-gold">Learn at Ravemora</span></h2>
            <p className="mt-8 text-lg text-offwhite/80 leading-relaxed max-w-xl">
              Discover coaching, mentoring, practical training, academic counselling and career guidance through our dedicated learning platform.
            </p>
            <p className="mt-4 text-offwhite/70 leading-relaxed max-w-xl">
              Learn from experienced professionals. Develop practical capabilities. Get personalised guidance.
            </p>
            <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" className="btn-gold mt-10 inline-flex items-center gap-2" data-testid="platform-cta">
              Visit Learn at Ravemora <ArrowUpRight size={14}/>
            </a>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/3] w-full overflow-hidden border border-gold/30">
              <img src={IMAGES.coaching} alt="Mentoring session" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
            </div>
          </div>
        </div>
      </section>

      {/* 12 — BECOME A MENTOR */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28" data-testid="learning-mentor">
        <div className="border border-navy/10 bg-slateBg p-10 md:p-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="label-eyebrow text-navy/60">For professionals</div>
            <h2 className="serif-display text-3xl md:text-4xl mt-4 leading-tight text-navy">Have Experience Worth Sharing?</h2>
            <p className="mt-6 text-navy/70 leading-relaxed max-w-2xl">
              Experienced professionals and skilled practitioners can share their knowledge with learners through live online one-to-one or group mentoring.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <a href={LEARN_MENTOR} target="_blank" rel="noopener noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="mentor-cta">
              Become a Mentor <ArrowUpRight size={14}/>
            </a>
          </div>
        </div>
      </section>

      {/* 13 — FINAL CTA */}
      <section className="relative overflow-hidden bg-offwhite border-t border-navy/10" data-testid="learning-final-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28 relative z-10">
          <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl leading-tight text-navy max-w-3xl">Your Next Step Starts With <span className="text-gold">the Right Guidance.</span></h2>
          <p className="mt-8 max-w-2xl text-lg text-navy/70 leading-relaxed">
            Whether you are exploring a career, developing a professional skill, learning technology, seeking expert guidance or simply looking to grow, Ravemora Learning connects you with people who can help you move forward.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="learning-final-explore">Explore Learn at Ravemora <ArrowUpRight size={14}/></a>
            <Link to="/contact" className="btn-outline" data-testid="learning-final-talk">Talk to Us <ArrowRight size={14}/></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
